import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

import { ApiResponseError } from '@/errors/ApiResponseError';
import { IHTTPRequestService } from '@/interfaces/IHTTPRequestService';
import { IApiResponseError } from '@/interfaces/api/IApiResponseError';
import { IRefreshSessionResponse } from '@/pages/auth/interfaces/IRefreshSessionResponse';
import { normalizeString } from '@/utils/normalize-string';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';

const cookies = new Cookies({ path: '/' });

function createErrorHandler(instance: AxiosInstance) {
	return async function handleAxiosError(
		error: unknown | Error | AxiosError<IApiResponseError>,
	) {
		if (!axios.isAxiosError<IApiResponseError>(error) || !error.response)
			return Promise.reject(error);

		const originalRequest = error.config;
		const { data, status } = error.response;
		
		// Safely check if error structure exists
		const errorData = data?.error;
		const shouldRefresh =
			status === 401 &&
			errorData?.title &&
			normalizeString(errorData.title) === normalizeString('Token expired');

		if (shouldRefresh && originalRequest) {
			const refreshResponse = await instance.post<IRefreshSessionResponse>(
				'/auth/refresh',
				{
					username: cookies.get(StoredCookies.USERNAME),
					refreshToken: cookies.get(StoredCookies.REFRESH_TOKEN),
				},
			);
			cookies.set(StoredCookies.ACCESS_TOKEN, refreshResponse.data.accessToken, { path: '/' });
			const authorization = `Bearer ${refreshResponse.data.accessToken}`;
			instance.defaults.headers.common['Authorization'] = authorization;
			originalRequest.headers.Authorization = authorization;
			return instance(originalRequest);
		}
		
		// Handle cases where error structure might be different
		if (errorData) {
			const detail = Array.isArray(errorData.detail) 
				? errorData.detail.join(', ') 
				: errorData.detail || 'An error occurred';
			return Promise.reject(
				new ApiResponseError(
					detail,
					errorData.source,
					errorData.status || String(status),
					errorData.title || 'Error',
				),
			);
		}
		
		// Fallback for unexpected error formats
		return Promise.reject(
			new ApiResponseError(
				'An unexpected error occurred',
				{ pointer: '' },
				String(status),
				'Error',
			),
		);
	};
}

export const axiosInstance = axios.create({
	baseURL: import.meta.env.VITE_API_URL,
	headers: {
		common: { Authorization: `Bearer ${cookies.get(StoredCookies.ACCESS_TOKEN)}` },
	},
});

axiosInstance.interceptors.response.use(
	(response) => response,
	createErrorHandler(axiosInstance),
);

function createAxiosService(
	instance: AxiosInstance,
): IHTTPRequestService<AxiosRequestConfig> {
	return {
		get: async <T,>(url: string, config?: AxiosRequestConfig): Promise<T> => {
			const response = await instance.get<T>(url, config);
			return response.data;
		},
		post: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			const response = await instance.post<T>(url, body, config);
			return response.data;
		},
		patch: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			const response = await axiosInstance.patch(url, body, config);
			return response.data;
		},
		put: async <T, K = unknown>(
			url: string,
			body: K,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			const response = await axiosInstance.put(url, body, config);
			return response.data;
		},
		delete: async <T,>(
			url: string,
			config?: AxiosRequestConfig,
		): Promise<T> => {
			const response = await axiosInstance.delete<T>(url, config);
			return response.data;
		},
		setAuthentication: (token: string) => {
			instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			return;
		},
	};
}

export const axiosService = createAxiosService(axiosInstance);
